import { forwardRef, memo, MutableRefObject, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import './index.scss'
import { useTranslation } from 'react-i18next'
import { MONITOR_COLUMNS } from '../data'
import Table from '../../../components/Table'
import { DTO_AreaMonitoring } from '../../../services/calls/types/area'
import useDynamicRef from '../../../hooks/useDynamicRef'
import Infobox from '../../../components/Infobox'
import Button from '../../../components/Button'
import { useNavigate } from 'react-router-dom'
import Icon from '../../../components/Icon'
import { COLOR } from '../../../data/variables'
import { ICON } from '../../../components/Icon/data'
import AreaNameColumn from './table/AreaNameColumn'
import NewArea from './NewArea'
import { useToast } from '../../../hooks/useToast'
import { useMap } from '../../../hooks/useMap'
import { saveArea } from './utils'
import UserContext from '../../../contexts/UserContext/context'
import { Draw, FlotiMapRef } from '../../../components/FlotiMap/type'
import { LAYER_TYPE } from '../../../components/FlotiMap'
import { DateTime } from 'luxon'
import { InputError } from '../../../components/Input/types'

type SidebarAreaMonitoring = {
  mapRef: MutableRefObject<FlotiMapRef>
  handleVisibility: (value: Record<number, boolean>) => void
  handleDrawing: (value: 'circle' | 'polygon' | null) => void
  onAreaChange: () => void
}

const AreaMonitoring_Sidebar = memo(forwardRef(({
  mapRef, handleVisibility, handleDrawing, onAreaChange
}: SidebarAreaMonitoring, ref) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const navigate = useNavigate()
  const { addToast } = useToast()
  const { degreesToRadians } = useMap()
  
  const [areas, setAreas] = useState<DTO_AreaMonitoring[]>([])
  const [visibility, setVisibility] = useState<Record<number, boolean>>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [shape, setShape] = useState<'circle' | 'polygon' | null>(null)
  const [draw, setDraw] = useState<Draw | null>(null)
  const [error, setError] = useState<Record<string, InputError>>()

  const forwardRefAction = useDynamicRef({
    getAreas: () => areas,
    setAreas,
    setIsLoading,
    setDraw,
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  useEffect(() => {
    handleVisibility(visibility)
  }, [visibility])

  useEffect(() => {
    let value = shape
    if (!editMode) value = null
    handleDrawing(value)
  }, [editMode, shape])

  const zoomToShape = useCallback((row: any) => {
    const { radius, areaid } = row
    mapRef.current.fitInMap(radius ? LAYER_TYPE.CIRCLES : LAYER_TYPE.POLYGONS, areaid)
  }, [mapRef])

  const formatFunction = useCallback((colId: string, val, row) => {
    if (colId === 'NAME') {
      return (
        <AreaNameColumn
          name={val}
          visible={row.visible}
          changeVisibility={async (vis) => {
            setVisibility({ ...visibility, [row.areaid]: vis })
            if (vis) zoomToShape(row)
          }}
        />
      )
    } else if (colId === 'REPORT') {
      const label = (
        <>
          {t('Report')}
          <Icon icon={ICON.LAUNCH} />
        </>
      )

      const from = DateTime.now().minus({ day: 1}).toFormat('YYYY-MM-DD')
      return (
        <Button
          label={label}
          type="tertiary"
          size="s"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/reports/area-monitoring?from=${from}&to=${from}&areaids=${row.areaid}`)
          }}
        />  
      )
    } else if (colId === 'FLEET') {
      const mapFleet = val.map((v) => {
        const fleet = user?.available_fleets.find((f) => f.fleetid === v)
        return fleet?.name ?? v
      })
      return mapFleet.join(', ')
    }
    return val
  }, [visibility, navigate, t, user, zoomToShape])

  const mappedHeaderCols = useMemo(() => {
    const lookup = {}
    Object.keys(MONITOR_COLUMNS).forEach((key) => {
      const { id } = MONITOR_COLUMNS[key]
      lookup[id] = {
        ...MONITOR_COLUMNS[key],
        formatFunction: (val, row) => formatFunction(id, val, row)
      }
    })
    return lookup
  }, [visibility, formatFunction])

  const rows = useMemo(() => {
    const rowArr = []
    if (areas.length) {
      areas.forEach((area) => {
        const newRow = { ...area }
        Object.keys(mappedHeaderCols).forEach((id) => {
          const { path } = mappedHeaderCols[id]
          const value = area[path]
          newRow[id] = value ?? ''
        })
        rowArr.push({ ...newRow, visible: visibility[area.areaid] ?? true })
      })
    }
    return rowArr
  }, [areas, mappedHeaderCols, visibility])

  const newButton = useMemo(() => (
    <Button
      type="primary"
      size="m"
      onClick={() => setEditMode(!editMode)}
      isActive={editMode}
      label={(
        <>
          {t('Draw a new area')}
          <Icon icon={editMode ? ICON.CLOSE : ICON.ADD} color={COLOR.WHITE} />
        </>
      )}
    />
  ), [editMode, t])

  const saveData = useCallback(async (value: any) => {
    const newData = {
      ...value,
      shapetype: draw?.type === 'circle' ? 'Circle' : 'Polygon',
      enabled: value.enabled ?? true
    }
    if (draw.type === 'circle') {
      newData.centerLon = draw.center[0]
      newData.centerLat = draw?.center[1]
      newData.radius = draw?.radius
    } else if (draw.type === 'polygon') {
      newData.polyshape = draw?.coordinates
    }

    const { status, data } = await saveArea(newData)
    if (status === 'success') {
      onAreaChange()
      setEditMode(false)
      addToast({
        status: "success",
        type: "toast",
        message: t('Area has been created')
      })
    } else {
      setError(data)
    }
  }, [draw, addToast, onAreaChange, degreesToRadians])

  return (
    <div className="AreaMonitoring__sidebar">
      <div className="AreaMonitoring__sidebar-title">{t('Area monitoring')}</div>
      <div className="AreaMonitoring__sidebar-subtitle">{t('Draw a new area or pick area from the list')}</div>
      <div className="AreaMonitoring__sidebar-new">
        {!editMode ? newButton : (
          <NewArea
            button={newButton}
            drawingCallback={(val) => {
              if (!val) setEditMode(false)
              setShape(val)
            }}
            saveData={(val) => saveData(val)}
          />
        )}
      </div>
      <Table
        isLoading={isLoading}
        title={`${areas.length ?? 0} ${t('saved areas')}`}
        showSearch={true}
        tableHeader={mappedHeaderCols}
        includePageFooter={true}
        rows={rows}
        onRowClick={zoomToShape}
        defaultSort={{ column: 'NAME', type: 'ascending' }}
      />

      <Infobox
        type="inline"
        status="info"
        label={t('Note')}
        closable={false}
        message={(
          <>
            {t('To see the activity in the monitored areas, click on the button below.')}
            <Button
              size="s"
              type="secondary"
              onClick={() => navigate('/reports/area-monitoring')}
              label={(
                <>
                  {t('Monitoring report')}
                  <Icon icon={ICON.LAUNCH} color={COLOR.WHITE} />
                </>
              )}
            />
          </>
        )}
      />
    </div>
  )
}))

export default AreaMonitoring_Sidebar
